/**
 * Custom hook for managing influencer data with local storage persistence
 * Provides manual update capabilities without backend requirements
 */
import { useState, useEffect } from 'react'

export interface Influencer {
  name: string
  audience: 'Kurdish' | 'Arab'
  followers: number
  engagementRate: number
  instagramLink: string
  profilePicture: string
  lastUpdated?: string
}

/**
 * Hook for managing influencer data with local storage
 */
export function useInfluencerData(initialData: Influencer[]) {
  const [influencers, setInfluencers] = useState<Influencer[]>(initialData)
  const [lastSync, setLastSync] = useState<string>('')

  // Load from local storage on mount
  useEffect(() => {
    const stored = localStorage.getItem('edicoo-influencers')
    const storedSync = localStorage.getItem('edicoo-last-sync')
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setInfluencers(parsed)
      } catch (error) {
        console.error('Error loading stored influencers:', error)
      }
    }
    
    if (storedSync) {
      setLastSync(storedSync)
    }
  }, [])

  /**
   * Update single influencer metrics
   */
  const updateInfluencer = (name: string, newFollowers: number, newEngagement: number) => {
    setInfluencers(prev => {
      const updated = prev.map(inf => 
        inf.name === name 
          ? { 
              ...inf, 
              followers: newFollowers, 
              engagementRate: newEngagement,
              lastUpdated: new Date().toISOString()
            }
          : inf
      )
      
      // Save to local storage
      localStorage.setItem('edicoo-influencers', JSON.stringify(updated))
      localStorage.setItem('edicoo-last-sync', new Date().toISOString())
      setLastSync(new Date().toISOString())
      
      return updated
    })
  }

  /**
   * Bulk import influencers
   */
  const importInfluencers = (newData: Influencer[]) => {
    setInfluencers(prev => {
      // Merge existing with new data, preferring new data for duplicates
      const merged = [...prev]
      newData.forEach(newInf => {
        const existingIndex = merged.findIndex(inf => inf.name === newInf.name)
        if (existingIndex >= 0) {
          merged[existingIndex] = { ...merged[existingIndex], ...newInf }
        } else {
          merged.push(newInf)
        }
      })

      localStorage.setItem('edicoo-influencers', JSON.stringify(merged))
      localStorage.setItem('edicoo-last-sync', new Date().toISOString())
      setLastSync(new Date().toISOString())
      
      return merged
    })
  }

  /**
   * Reset to initial data
   */
  const resetToInitial = () => {
    setInfluencers(initialData)
    localStorage.removeItem('edicoo-influencers')
    localStorage.removeItem('edicoo-last-sync')
    setLastSync('')
  }

  return {
    influencers,
    lastSync,
    updateInfluencer,
    importInfluencers,
    resetToInitial
  }
}
